import _extends from '@babel/runtime/helpers/esm/extends';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React from 'react';
import { extendTheme, useColorMode, useTheme, ThemeProvider } from '@chakra-ui/react';
import propTypes from 'prop-types';
import defaultTheme from '@chakra-ui/theme';
import { runIfFn } from '@chakra-ui/utils';
import merge from 'lodash.merge';
import { diff } from 'deep-object-diff';
import capsize, { getCapHeight } from 'capsize';

var generate = function generate(_ref) {
  var sizes = _ref.sizes,
    fontMetrics = _ref.fontMetrics;
  var result = {};
  for (var size in sizes) {
    var capHeight = getCapHeight({
      fontSize: sizes[size].fontSize,
      fontMetrics: fontMetrics
    });
    var none = capsize({
      fontSize: sizes[size].fontSize,
      lineGap: 0,
      fontMetrics: fontMetrics
    });
    var noneLineHeight = parseFloat(none.lineHeight);
    var lineHeightStep = (sizes[size].leading - noneLineHeight) / 3;
    var shortLineHeight = noneLineHeight + lineHeightStep;
    var tallLineHeight = sizes[size].leading + lineHeightStep * 3;
    var _short = capsize({
      fontSize: sizes[size].fontSize,
      leading: shortLineHeight,
      fontMetrics: fontMetrics
    });
    var base = capsize({
      fontSize: sizes[size].fontSize,
      leading: sizes[size].leading,
      fontMetrics: fontMetrics
    });
    var tall = capsize({
      fontSize: sizes[size].fontSize,
      leading: tallLineHeight,
      fontMetrics: fontMetrics
    });
    result[size] = {
      capHeight: capHeight,
      base: none,
      leading: {
        "short": diff(none, _short),
        base: diff(none, base),
        tall: diff(none, tall)
      }
    };
  }
  return result;
};

var colors = {
  /* Outdoorly Red */
  red: {
    50: '#fff1f0',
    100: '#FFE0E0',
    200: '#ffc7c7',
    300: '#ff9ea1',
    400: '#ff757e',
    500: '#ff4c5f',
    600: '#d9364c',
    700: '#b3243c',
    800: '#8c152d',
    900: '#660e23'
  },
  /* Dust Red */
  danger: {
    50: '#fff1f0',
    100: '#ffccc7',
    200: '#ffa39e',
    300: '#ff7875',
    400: '#ff4d4f',
    500: '#f5222d',
    600: '#cf1322',
    700: '#a8071a',
    800: '#820014',
    900: '#5c0011'
  },
  blue: {
    50: '#F3FAFF',
    100: '#D8E7F3',
    200: '#B1D0E7',
    300: '#8AB8DB',
    400: '#63A1CF',
    500: '#3C89C3',
    600: '#306E9C',
    700: '#245275',
    800: '#18374E',
    900: '#0C1B27'
  },
  /* Neutral Colors */
  gray: {
    50: '#FAFAFA',
    75: '#F5F5F5',
    100: '#EEEEEE',
    200: '#E1E1E1',
    300: '#C8C8C8',
    400: '#A2A2A2',
    500: '#888888',
    600: '#626262',
    700: '#484848',
    800: '#3C3C3C',
    900: '#2F2F2F'
  },
  white: '#FDFDFD',
  black: '#222222',
  /* Secondary Colors */
  espresso: {
    50: '#FBFAF7',
    100: '#F3F0EA',
    200: '#EFE9E0',
    300: '#DDD0BF',
    400: '#CBB89E',
    500: '#B9A07D',
    600: '#A7885C',
    700: '#876D49',
    800: '#55452E',
    900: '#453825'
  },
  teal: {
    50: '#F4FBFC',
    100: '#DBEDF0',
    200: '#B8DBE0',
    300: '#94C9D1',
    400: '#5FAEBA',
    500: '#4594A0',
    600: '#3D848E',
    700: '#2E636B',
    800: '#1F4247',
    900: '#173135'
  },
  sage: {
    50: '#F5F7F5',
    100: '#E1E7E1',
    200: '#C4D1C5',
    300: '#B6C6B7',
    400: '#9AAF9A',
    500: '#7D997E',
    600: '#647F65',
    700: '#4D634E',
    800: '#374637',
    900: '#2C382C'
  },
  emerald: {
    50: '#F5F9F5',
    100: '#E8F2E8',
    200: '#C7E0C8',
    300: '#A6CEA7',
    400: '#85BC87',
    500: '#64AA66',
    600: '#4E8D4F',
    700: '#3C6C3D',
    800: '#294C2A',
    900: '#203B21'
  },
  gold: {
    50: '#FFFCF6',
    100: '#FDF5E5',
    200: '#FBEDCD',
    300: '#F7DC9E',
    400: '#F3CB6F',
    500: '#EFBA40',
    600: '#E9A813',
    700: '#D19712',
    800: '#B98610',
    900: '#A2750E'
  },
  tangerine: {
    50: '#FFFBF7',
    100: '#FEEFDE',
    200: '#FDE3C5',
    300: '#FDD7AC',
    400: '#FBC07B',
    500: '#FAA849',
    600: '#F99017',
    700: '#D77606',
    800: '#BE6905',
    900: '#A55B05'
  },
  salmon: {
    50: '#FDF8F7',
    100: '#F8E7E3',
    200: '#F3D5CF',
    300: '#EEC4BA',
    400: '#E3A192',
    500: '#D97E69',
    600: '#CF5B40',
    700: '#AF452C',
    800: '#873522',
    900: '#722D1D'
  },
  violet: {
    50: '#F8F7FA',
    100: '#EBE7F0',
    200: '#DFD8E6',
    300: '#D2C8DC',
    400: '#B8A9C8',
    500: '#9F8AB4',
    600: '#856BA0',
    700: '#6C5484',
    800: '#524065',
    900: '#463655'
  }
};
colors.whiteAlpha = defaultTheme.colors.whiteAlpha;
colors.blackAlpha = defaultTheme.colors.blackAlpha;

// Map colours that are set by Chakra by default in some components (for example
// Alert) to our own defined colours, otherwise we'd see `green.500` trying to
// be set in the HTML
colors.green = colors.emerald;
colors.orange = colors.gold;

// Fully customize the sizes of inputs
var inputSizes = {
  lg: {
    px: 3,
    borderRadius: 'xl',
    h: 12
  },
  md: {
    px: 3,
    borderRadius: 'lg',
    h: 10
  },
  sm: {
    px: 3,
    borderRadius: 'lg',
    h: 8
  }
};
var input = _objectSpread(_objectSpread({}, defaultTheme.components.Input), {}, {
  sizes: {
    lg: {
      field: inputSizes.lg,
      addon: inputSizes.lg
    },
    md: {
      field: inputSizes.md,
      addon: inputSizes.md
    },
    sm: {
      field: inputSizes.sm,
      addon: inputSizes.sm
    }
  },
  // NOTE: We've purposely removed all but the 'outline' variant as we don't
  // need them so don't want to accidentally use unstyled variants.
  variants: {
    // Have to overwrite colours here and not on the component due to
    // specificity issues with focus & error colors.
    outline: function outline(props) {
      return merge(runIfFn(defaultTheme.components.Input.variants.outline, props), {
        field: {
          borderColor: 'scheme.border',
          _hover: {
            borderColor: 'scheme.controlHover'
          },
          _disabled: {
            borderColor: 'scheme.controlDisabled',
            bg: 'scheme.controlDisabledBg'
          },
          _invalid: {
            boxShadow: 'none',
            bg: 'scheme.controlErrorBg'
          },
          _placeholder: {
            color: 'scheme.textPlaceholder'
          }
        }
      });
    }
  },
  defaultProps: _objectSpread(_objectSpread({}, defaultTheme.components.Input.defaultProps), {}, {
    background: 'scheme.bgPrimary',
    borderRadius: 'lg',
    errorBorderColor: 'scheme.controlError',
    focusBorderColor: 'scheme.focus',
    px: 3,
    size: 'lg'
  })
});

// Fully customize the sizes of textareas
var textareaSizes = {
  lg: {
    px: 3,
    borderRadius: 'lg',
    h: 12
  },
  md: {
    px: 3,
    borderRadius: 'lg',
    h: 10
  },
  sm: {
    px: 3,
    borderRadius: 'lg',
    h: 8
  }
};
var textarea = _objectSpread(_objectSpread({}, defaultTheme.components.Textarea), {}, {
  sizes: {
    lg: textareaSizes.lg,
    md: textareaSizes.md,
    sm: textareaSizes.sm
  },
  // NOTE: We've purposely removed all but the 'outline' variant as we don't
  // need them so don't want to accidentally use unstyled variants.
  variants: {
    // Have to overwrite colours here and not on the component due to
    // specificity issues with focus & error colors.
    outline: function outline(props) {
      return merge(runIfFn(defaultTheme.components.Textarea.variants.outline, props), {
        borderColor: 'scheme.border',
        _hover: {
          borderColor: 'scheme.controlHover'
        },
        _disabled: {
          borderColor: 'scheme.controlDisabled',
          bg: 'scheme.controlDisabledBg'
        },
        _invalid: {
          boxShadow: 'none',
          bg: 'scheme.controlErrorBg'
        },
        _placeholder: {
          color: 'scheme.textPlaceholder'
        }
      });
    }
  },
  defaultProps: _objectSpread(_objectSpread({}, defaultTheme.components.Textarea.defaultProps), {}, {
    background: 'scheme.controlActiveBg',
    borderRadius: 'lg',
    errorBorderColor: 'scheme.controlError',
    focusBorderColor: 'scheme.focus',
    px: 3
  })
});
var form = _objectSpread(_objectSpread({}, defaultTheme.components.Form), {}, {
  baseStyle: function baseStyle(props) {
    var defaultBaseStyle = runIfFn(defaultTheme.components.Form.baseStyle, props);

    // undoing some of the odd styling choices ChakraUI made here
    delete defaultBaseStyle.helperText.mt;
    delete defaultBaseStyle.helperText.lineHeight;
    delete defaultBaseStyle.helperText.fontSize;
    return merge(defaultBaseStyle, {
      errorText: {
        color: 'scheme.textError'
      }
    });
  }
});
var formLabel = _objectSpread(_objectSpread({}, defaultTheme.components.Form), {}, {
  baseStyle: function baseStyle(props) {
    var defaultBaseStyle = runIfFn(defaultTheme.components.Form.baseStyle, props);

    // undoing some of the odd styling choices ChakraUI made here
    delete defaultBaseStyle.mr;
    delete defaultBaseStyle.mb;
    delete defaultBaseStyle.fontSize;
    return defaultBaseStyle;
  }
});
var radio = _objectSpread(_objectSpread({}, defaultTheme.components.Radio), {}, {
  defaultProps: {
    size: 'md',
    colorScheme: 'brand'
  },
  sizes: merge(defaultTheme.components.Radio.sizes, {
    sm: {
      control: {
        w: 4,
        h: 4
      }
    },
    md: {
      control: {
        w: 5,
        h: 5
      }
    },
    lg: {
      control: {
        h: 6,
        w: 6
      }
    }
  }),
  baseStyle: function baseStyle(props) {
    var defaultBaseStyle = runIfFn(defaultTheme.components.Radio.baseStyle, props);
    return merge(defaultBaseStyle, {
      label: {
        width: props.isFullWidth ? 'full' : null
      },
      control: {
        borderColor: 'scheme.border',
        '.chakra-radio:hover > &': {
          borderColor: 'scheme.controlHover'
        },
        _checked: {
          bg: 'scheme.controlActiveBg',
          borderColor: 'scheme.controlActive',
          _before: {
            bg: 'scheme.controlActive'
          },
          '.chakra-radio:hover > &': {
            bg: 'scheme.controlActiveBg',
            borderColor: 'scheme.controlActive',
            _before: {
              bg: 'scheme.controlActive'
            }
          }
        },
        _disabled: {
          borderColor: 'scheme.border',
          bg: 'scheme.bgSecondary',
          '.chakra-radio:hover > &': {
            borderColor: 'scheme.border',
            bg: 'scheme.bgSecondary',
            _before: {
              bg: 'scheme.controlDisabledBg'
            }
          },
          _checked: {
            borderColor: 'scheme.border',
            bg: 'scheme.bgSecondary',
            _before: {
              bg: 'scheme.controlDisabledBg'
            }
          }
        },
        _invalid: {
          bg: 'scheme.controlErrorBg',
          borderColor: 'scheme.controlError',
          _before: {
            bg: 'scheme.controlError'
          },
          '.chakra-radio:hover > &': {
            bg: 'scheme.controlErrorBg',
            borderColor: 'scheme.controlActive',
            _before: {
              bg: 'scheme.controlActive'
            }
          }
        }
      }
    });
  }
});
var button = {
  variants: {
    subtle: function subtle(props) {
      return merge(runIfFn(defaultTheme.components.Button.variants.outline, props), {
        borderWidth: '0',
        bg: "".concat(props.colorScheme, ".50"),
        _hover: {
          bg: "".concat(props.colorScheme, ".100")
        },
        _active: {
          bg: "".concat(props.colorScheme, ".200")
        }
      });
    },
    outline: function outline(props) {
      return {
        borderColor: "".concat(props.colorScheme, ".200"),
        _hover: {
          borderColor: "".concat(props.colorScheme, ".300"),
          bg: "".concat(props.colorScheme, ".50")
        },
        _active: {
          bg: "".concat(props.colorScheme, ".100")
        },
        color: "".concat(props.colorScheme, ".600")
      };
    }
  },
  baseStyle: {
    borderRadius: 'lg',
    padding: 4
  }
};
var checkbox = _objectSpread(_objectSpread({}, defaultTheme.components.Checkbox), {}, {
  defaultProps: {
    size: 'md',
    colorScheme: 'brand'
  },
  baseStyle: function baseStyle(props) {
    var defaultBaseStyle = runIfFn(defaultTheme.components.Checkbox.baseStyle, props);
    return merge(defaultBaseStyle, {
      label: {
        width: props.isFullWidth ? 'full' : null
      },
      control: {
        borderRadius: 'md',
        borderColor: 'scheme.border',
        '.chakra-checkbox:hover > &': {
          borderColor: 'scheme.controlHover'
        },
        _checked: {
          bg: 'scheme.controlActive',
          borderColor: 'scheme.controlActive',
          color: 'scheme.bgPrimary',
          _before: {
            bg: 'scheme.controlActive'
          },
          '.chakra-checkbox:hover > &': {
            bg: 'scheme.controlActive',
            borderColor: 'scheme.controlActive',
            _before: {
              bg: 'scheme.controlActive'
            }
          }
        },
        _disabled: {
          borderColor: 'scheme.border',
          bg: 'scheme.bgSecondary',
          '.chakra-checkbox:hover > &': {
            borderColor: 'scheme.border',
            bg: 'scheme.bgSecondary',
            _before: {
              bg: 'scheme.controlDisabledBg'
            }
          },
          _checked: {
            borderColor: 'scheme.border',
            bg: 'scheme.bgSecondary',
            _before: {
              bg: 'scheme.controlDisabledBg'
            }
          }
        }
      }
    });
  },
  variants: {
    'white-background': {
      control: {
        bg: 'white'
      }
    }
  }
});
var heading = {
  baseStyle: {
    fontWeight: '500'
  }
};
var link = {
  baseStyle: {
    color: 'scheme.textPrimary'
  }
};
var popper = {
  parts: ['content'],
  baseStyle: {
    content: {
      _focus: {
        boxShadow: 'none'
      },
      _focusVisible: {
        boxShadow: 'none'
      }
    }
  },
  variants: {
    responsive: {
      popper: {
        maxWidth: 'unset',
        width: 'unset'
      }
    },
    'OD-tooltip': {
      body: {
        boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.2), 0px 2px 10px rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        border: 0
      },
      content: {
        borderRadius: '8px',
        '& .chakra-popover__arrow-positioner': {
          zIndex: '2 !important',
          //override inline style for arrow position.
          left: '-4px'
        },
        marginLeft: 'md',
        border: 0
      },
      popper: {
        zIndex: defaultTheme.zIndices.popover
      }
    }
  }
};
var accordion = {
  parts: ['button'],
  baseStyle: {
    button: {
      _focus: {
        boxShadow: 'none'
      },
      _focusVisible: {
        boxShadow: 'outline'
      }
    }
  }
};
var outdoorly = extendTheme({
  styles: {
    global: {
      '.smooth-filter': {
        transition: 'filter .1s ease-in'
      },
      '.darken': {
        mixBlendMode: 'darken'
      },
      strong: {
        fontWeight: '500'
      }
    }
  },
  fonts: {
    heading: 'Rubik, sans-serif',
    body: 'Rubik, sans-serif',
    mono: 'monospace'
  },
  space: _objectSpread(_objectSpread({}, defaultTheme.space), {}, {
    none: defaultTheme.space['0'],
    sm: defaultTheme.space['1'],
    md: defaultTheme.space['2'],
    lg: defaultTheme.space['3'],
    xl: defaultTheme.space['4'],
    '2xl': defaultTheme.space['5'],
    '3xl': defaultTheme.space['6'],
    gutter: defaultTheme.space['4']
  }),
  sizes: _objectSpread(_objectSpread({}, defaultTheme.sizes), {}, {
    '7xl': '80rem',
    page: '62rem'
  }),
  colors: colors,
  radii: {
    none: '0',
    sm: '0.125rem',
    md: '0.25rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px'
  },
  shadows: _objectSpread(_objectSpread({}, defaultTheme.shadows), {}, {
    button: 'inset 0px 2px 0px rgba(0, 0, 0, 0.05)',
    buttonVariant: '0px 1px 0px rgba(0, 0, 0, 0.05), inset 0px -1px 0px rgba(0, 0, 0, 0.2)',
    tooltip: '0px 0px 2px rgba(0, 0, 0, 0.2), 0px 2px 10px rgba(0, 0, 0, 0.1)'
  }),
  components: _objectSpread(_objectSpread({}, defaultTheme.components), {}, {
    Link: link,
    Button: button,
    Radio: radio,
    Form: form,
    FormLabel: formLabel,
    Input: input,
    Textarea: textarea,
    Checkbox: checkbox,
    Heading: heading,
    Popover: popper,
    Accordion: accordion
  }),
  /**
   * Color Schemes follow the Gravity 3 Color System
   * https://medium.com/buildit/gravity-3-colour-system-a41cf3fbf443
   *
   * Each scheme is classified as either "light" or "dark" to match support for
   * darkmode by Chakra UI.
   */
  colorSchemes: {
    light: {
      "default": {
        textPrimary: colors.black,
        textSecondary: colors.gray['600'],
        textPrimaryInverse: colors.white,
        textSecondaryInverse: colors.gray['200'],
        textPlaceholder: colors.gray['400'],
        textDisabled: colors.gray['400'],
        textWarning: colors.gold['600'],
        textError: colors.danger['700'],
        textMoneyPositive: colors.emerald['500'],
        textMoneyNegative: colors.red['800'],
        priceRegular: colors.gray['600'],
        priceDiscounted: colors.red['500'],
        priceSale: colors.red['500'],
        controlHover: colors.red['300'],
        controlActive: colors.red['500'],
        controlActiveBg: colors.white,
        controlInactive: colors.gray['500'],
        controlInactiveBg: colors.white,
        controlError: colors.danger['700'],
        controlErrorBg: colors.danger['100'],
        controlDisabled: colors.gray['300'],
        controlDisabledBg: colors.gray['200'],
        iconRegular: colors.red['500'],
        focus: colors.blue['400'],
        bgPrimary: colors.white,
        bgSecondary: colors.gray['100'],
        bgPrimaryInverse: colors.blue['800'],
        border: colors.gray['200']
      }
    },
    dark: {}
  },
  /**
   * Definitions of fontSize + lineHeight combos while removing the default
   * spacing browsers wrap around text.
   *
   * Size scale follows Chakra UI's scale (xs, sm, md, lg, ...etc).
   *
   * Each font can have a different set of values.
   *
   * Leading (aka lineHeight) determines the spacing from one base line to
   * another. It follows a quadratic progression
   *
   * Values are calulated using the Capsize tool:
   * https://github.com/seek-oss/capsize
   *
   * The output of this will show up in the `<Text>` component
   */
  typography: generate({
    // Metrics generated via https://seek-oss.github.io/capsize/
    fontMetrics: {
      capHeight: 700,
      ascent: 935,
      descent: -250,
      lineGap: 0,
      unitsPerEm: 1000
    },
    sizes: {
      xs: {
        fontSize: 10,
        leading: 18
      },
      sm: {
        fontSize: 12,
        leading: 20
      },
      md: {
        fontSize: 14,
        leading: 22
      },
      lg: {
        fontSize: 16,
        leading: 24
      },
      xl: {
        fontSize: 20,
        leading: 28
      },
      '2xl': {
        fontSize: 24,
        leading: 32
      },
      '3xl': {
        fontSize: 30,
        leading: 38
      },
      '4xl': {
        fontSize: 38,
        leading: 46
      },
      '5xl': {
        fontSize: 46,
        leading: 54
      },
      '6xl': {
        fontSize: 56,
        leading: 64
      },
      '7xl': {
        fontSize: 68,
        leading: 72
      },
      '8xl': {
        fontSize: 80,
        leading: 84
      }
    }
  })
});

var _excluded = ["scheme"];
var ColorScheme = function ColorScheme(_ref) {
  var _theme$colorSchemes;
  var scheme = _ref.scheme,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useColorMode = useColorMode(),
    colorMode = _useColorMode.colorMode;
  var theme = useTheme();
  var schemeName;
  if (scheme) {
    // An explicit scheme is passed in, so use that
    schemeName = scheme;
  } else if (theme.colors.schemeName) {
    // No explicit scheme is passed in, so we continue using the existing
    // selected one if it exists
    schemeName = theme.colors.schemeName;
  } else {
    // If no scheme passed in and none already set, use 'default'
    schemeName = 'default';
  }
  var schemeData = (_theme$colorSchemes = theme.colorSchemes) === null || _theme$colorSchemes === void 0 || (_theme$colorSchemes = _theme$colorSchemes[colorMode]) === null || _theme$colorSchemes === void 0 ? void 0 : _theme$colorSchemes[schemeName];

  // Aliasing old color pack names
  if (schemeData) {
    schemeData.a = schemeData.bg;
    schemeData.b = schemeData.fg;
  }
  return /*#__PURE__*/React.createElement(ThemeProvider, _extends({}, props, {
    theme: _objectSpread(_objectSpread({}, theme), {}, {
      colors: _objectSpread(_objectSpread({}, theme.colors), {}, {
        schemeName: scheme,
        scheme: schemeData
      })
    })
  }));
};
ColorScheme.propTypes = {
  /** Default: 'default' */
  scheme: propTypes.oneOf(['default', 'contrast']),
  children: propTypes.node.isRequired
};

export { ColorScheme, outdoorly };
